import { formatRecordedDate } from '@/utils/recordedValues'
/**
 * Audit Log Table Component
 * Displays audit log entries in a table format with expandable details
 */

import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  User,
  LogIn,
  LogOut,
  UserPlus,
  UserMinus,
  Shield,
  Settings,
  Key,
  AlertTriangle,
  Globe,
  Clock,
} from 'lucide-react'
import type { AuditLogEntry, AuditAction } from '@/types/session.types'
import { formatRelativeTime } from '@/types/session.types'

// ============================================================================
// TYPES
// ============================================================================

export interface AuditLogTableProps {
  entries: AuditLogEntry[]
  isLoading?: boolean
  showUser?: boolean
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getActionIcon(action: AuditAction): React.ElementType {
  switch (action) {
    case 'login':
      return LogIn
    case 'logout':
      return LogOut
    case 'login_failed':
      return AlertTriangle
    case 'user_created':
    case 'user_invited':
      return UserPlus
    case 'user_updated':
      return User
    case 'user_deleted':
      return UserMinus
    case 'role_assigned':
    case 'role_revoked':
      return Shield
    case 'password_changed':
    case 'password_reset':
      return Key
    case 'session_terminated':
      return LogOut
    case 'settings_updated':
      return Settings
    default:
      return Settings
  }
}

function getActionLabel(action: AuditAction): string {
  const labels: Partial<Record<AuditAction, string>> = {
    login: 'Login',
    logout: 'Logout',
    login_failed: 'Failed Login',
    user_created: 'User Created',
    user_updated: 'User Updated',
    user_deleted: 'User Deleted',
    user_invited: 'User Invited',
    user_activated: 'User Activated',
    user_deactivated: 'User Deactivated',
    user_locked: 'User Locked',
    user_unlocked: 'User Unlocked',
    role_assigned: 'Role Assigned',
    role_revoked: 'Role Revoked',
    password_changed: 'Password Changed',
    password_reset: 'Password Reset',
    password_reset_requested: 'Password Reset Requested',
    password_reset_completed: 'Password Reset Completed',
    session_terminated: 'Session Terminated',
    session_created: 'Session Created',
    all_sessions_terminated: 'All Sessions Terminated',
    settings_updated: 'Settings Updated',
    tenant_updated: 'Tenant Updated',
    subscription_changed: 'Subscription Changed',
    invitation_sent: 'Invitation Sent',
    invitation_accepted: 'Invitation Accepted',
    invitation_cancelled: 'Invitation Cancelled',
    invitation_expired: 'Invitation Expired',
    mfa_enabled: 'MFA Enabled',
    mfa_disabled: 'MFA Disabled',
    api_key_created: 'API Key Created',
    api_key_revoked: 'API Key Revoked',
  }
  return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

function getActionColor(action: AuditAction): string {
  switch (action) {
    case 'login':
    case 'user_created':
    case 'user_invited':
    case 'role_assigned':
      return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400'
    case 'logout':
    case 'session_terminated':
      return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400'
    case 'login_failed':
    case 'user_deleted':
    case 'role_revoked':
      return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400'
    case 'password_changed':
    case 'password_reset':
      return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400'
    default:
      return 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-400'
  }
}

// ============================================================================
// ENTRY ROW COMPONENT
// ============================================================================

interface EntryRowProps {
  entry: AuditLogEntry
  showUser: boolean
}

function EntryRow({ entry, showUser }: EntryRowProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const ActionIcon = getActionIcon(entry.action)
  const hasDetails = entry.details !== undefined && entry.details !== null

  return (
    <>
      <tr
        className={`hover:bg-surface-overlay/50 transition-colors ${
          hasDetails ? 'cursor-pointer' : ''
        }`}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        {/* Expand Toggle */}
        <td className="w-10 px-4 py-3">
          {hasDetails ? (
            <button
              type="button"
              aria-label="Toggle recorded details"
              className="p-1 hover:bg-surface-overlay rounded"
              onClick={e => {
                e.stopPropagation()
                setIsExpanded(!isExpanded)
              }}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-text-tertiary" />
              ) : (
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              )}
            </button>
          ) : (
            <div className="w-6" />
          )}
        </td>

        {/* Timestamp */}
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-text-tertiary" />
            <span className="text-text-primary">
              {formatRecordedDate(entry.createdAt, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          <p className="text-xs text-text-tertiary mt-0.5">{formatRelativeTime(entry.createdAt)}</p>
        </td>

        {/* Action */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded ${getActionColor(entry.action)}`}>
              <ActionIcon className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-text-primary">
              {getActionLabel(entry.action)}
            </span>
          </div>
        </td>

        {/* User */}
        {showUser && (
          <td className="px-4 py-3">
            {entry.userEmail ? (
              <div>
                <p className="text-sm text-text-primary">{entry.performedByName || 'Unknown'}</p>
                <p className="text-xs text-text-tertiary">{entry.userEmail}</p>
              </div>
            ) : (
              <span className="text-sm text-text-tertiary">System</span>
            )}
          </td>
        )}

        {/* Resource */}
        <td className="px-4 py-3">
          {entry.resourceType ? (
            <div>
              <span className="badge badge-ghost badge-sm capitalize">{entry.resourceType}</span>
              {entry.resourceId && (
                <p className="text-xs text-text-tertiary mt-0.5 font-mono">
                  {entry.resourceId.slice(0, 8)}...
                </p>
              )}
            </div>
          ) : (
            <span className="text-text-tertiary">-</span>
          )}
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          {entry.success === true ? (
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Success</span>
            </div>
          ) : entry.success === false ? (
            <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <XCircle className="w-4 h-4" />
              <span className="text-sm">Failed</span>
            </div>
          ) : (
            <span className="text-sm text-text-tertiary">Not recorded</span>
          )}
        </td>

        {/* IP Address */}
        <td className="px-4 py-3 hidden lg:table-cell">
          {entry.ipAddress ? (
            <div className="flex items-center gap-1 text-sm text-text-secondary">
              <Globe className="w-3.5 h-3.5" />
              <span>{entry.ipAddress}</span>
            </div>
          ) : (
            <span className="text-text-tertiary">-</span>
          )}
        </td>
      </tr>

      {/* Expanded Details */}
      {isExpanded && hasDetails && (
        <tr>
          <td colSpan={showUser ? 7 : 6} className="px-4 py-3 bg-surface-overlay/50">
            <div className="ml-10 p-3 rounded-lg bg-surface-light dark:bg-surface-dark border border-surface-overlay">
              <h4 className="text-xs font-medium text-text-tertiary uppercase mb-2">Details</h4>
              <pre className="text-sm text-text-primary whitespace-pre-wrap break-words">
                {JSON.stringify(entry.details, null, 2)}
              </pre>
              {entry.errorMessage && (
                <div className="mt-3 p-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    <strong>Error:</strong> {entry.errorMessage}
                  </p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AuditLogTable({ entries, isLoading = false, showUser = true }: AuditLogTableProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="card card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-sticky-head w-full">
            <thead className="bg-surface-overlay">
              <tr className="text-sm font-semibold text-text-primary">
                <th className="w-10"></th>
                <th className="text-left">Time</th>
                <th className="text-left">Action</th>
                {showUser && <th className="text-left">User</th>}
                <th className="text-left">Resource</th>
                <th className="text-left">Status</th>
                <th className="text-left hidden lg:table-cell">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-overlay">
              {[1, 2, 3, 4, 5].map(i => (
                <tr key={i}>
                  <td className="px-4 py-3">
                    <div className="skeleton w-4 h-4 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="skeleton h-4 rounded w-24" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="skeleton h-4 rounded w-28" />
                  </td>
                  {showUser && (
                    <td className="px-4 py-3">
                      <div className="skeleton h-4 rounded w-32" />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="skeleton h-4 rounded w-20" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="skeleton h-4 rounded w-16" />
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="skeleton h-4 rounded w-24" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Empty state
  if (entries.length === 0) {
    return (
      <div className="card card-elevated p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-surface-overlay flex items-center justify-center mx-auto mb-4">
          <Settings className="w-8 h-8 text-text-tertiary" />
        </div>
        <p className="text-lg font-medium text-text-primary mb-2">No activity found</p>
        <p className="text-text-secondary">There are no audit log entries matching your filters.</p>
      </div>
    )
  }

  return (
    <div className="card card-elevated overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table table-sticky-head w-full">
          <thead className="bg-surface-overlay">
            <tr className="text-sm font-semibold text-text-primary">
              <th className="w-10"></th>
              <th className="text-left">Time</th>
              <th className="text-left">Action</th>
              {showUser && <th className="text-left">User</th>}
              <th className="text-left">Resource</th>
              <th className="text-left">Status</th>
              <th className="text-left hidden lg:table-cell">IP Address</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-overlay">
            {entries.map(entry => (
              <EntryRow key={entry.id} entry={entry} showUser={showUser} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
